import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "motion/react";
import { spring } from "motion";
import {
  Button,
  Flex,
  Form,
  InputNumber,
  Progress,
  Radio,
  Select,
  theme,
} from "antd";
import BreadcrumbProposalStep from "../../../BreadcrumbProposalStep";
import MotionHoverAccent from "../../../../../../../../utils/animation/motion/MotionHoverAccent";

import StylesContainer from "./style";

import { PROPOSAL_DEFAULT_DATA_LEVEL } from "../../../../constant";
import { LIST_STEP_DATA } from "./constant";

import {
  cleanCurrencyValue,
  getCurrencyValue,
  selectValue,
} from "../../../../../../../../utils/helper/general";
import { getModelIdByRiskTolerance } from "../../../../../advisor/helper";

import {
  faClose,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const QuestionnaireContentLevel = ({
  handleClose,
  handleCreateProposal,
  open,
  userName,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showAnimationForm, setShowAnimationForm] = useState(true);

  const investmentObjective = Form.useWatch("investmentObjective", form);
  const riskTolerance = Form.useWatch("riskTolerance", form);

  const activeStepCopy = LIST_STEP_DATA[currentStepIndex];

  useEffect(() => {
    if (open) {
      setCurrentStepIndex(0);
      form.setFieldsValue(PROPOSAL_DEFAULT_DATA_LEVEL);

      setTimeout(() => setShowAnimationForm(false), 500);
    }
  }, [open]);

  useEffect(() => {
    form.setFieldValue(
      "recommendedModelId",
      getModelIdByRiskTolerance(riskTolerance)
    );
  }, [riskTolerance]);

  const submitAddProposal = () => {
    handleCreateProposal({
      ...PROPOSAL_DEFAULT_DATA_LEVEL,
      ...form.getFieldsValue(),
    });
  };

  const onClickBack = () => {
    setCurrentStepIndex(currentStepIndex - 1);
  };

  const onClickForward = () => {
    if (currentStepIndex === 2) {
      submitAddProposal();
    } else {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const Header = () => (
    <header>
      <article
        style={{
          background: token.proposal_header__background,
          borderBottom: "1px solid #15212A",
        }}
      >
        <img alt="Logo" src={token.logo_src} />
      </article>
      <article style={{ background: "rgba(22, 36, 45, 0.25)" }}>
        <h4>
          <span style={{ color: "#CFD9E3", fontWeight: 400 }}>Proposal: </span>
          {userName}
        </h4>

        <Flex align="center" gap={32} justify="space-between">
          <BreadcrumbProposalStep activeStep="start" />
          <Button
            className="button-close"
            icon={
              <FontAwesomeIcon icon={faClose} style={{ color: "#052C2B" }} />
            }
            onClick={handleClose}
            shape="circle"
            style={{ border: "unset" }}
          />
        </Flex>
      </article>
    </header>
  );

  return (
    <StylesContainer align="center" token={token} vertical>
      <Header />

      <main
        style={{
          transition: `transform ${spring(1, 0.4)}`,
          transform: showAnimationForm ? "translateY(100vh)" : "unset",
        }}
      >
        <div>
          <span>
            {currentStepIndex + 1}/{LIST_STEP_DATA.length}
          </span>
          <Progress
            percent={(100 / LIST_STEP_DATA.length) * (currentStepIndex + 1)}
            showInfo={false}
            strokeColor="#84B8C8"
            trailColor="#fff"
          />
        </div>

        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          initial={{ opacity: 0.3, scale: 0.8 }}
          key={currentStepIndex}
          transition={{
            scale: { type: "spring", visualDuration: 1.2 },
          }}
        >
          <Flex gap={32} vertical={currentStepIndex !== 1}>
            <Flex gap={12} vertical>
              <h2>{activeStepCopy.title}</h2>
              <p
                dangerouslySetInnerHTML={{
                  __html: activeStepCopy.description,
                }}
              />
            </Flex>

            <Form id="levelQuestionnaireForm" form={form} layout="vertical">
              <Form.Item
                hidden={currentStepIndex !== 0}
                name="investmentObjective"
              >
                <Radio.Group>
                  <Flex gap={16} vertical>
                    <Radio value="growth">Growth</Radio>
                    <Radio value="income">Income</Radio>
                    <Radio value="growth_income">Growth & Income</Radio>
                  </Flex>
                </Radio.Group>
              </Form.Item>
              <Flex justify="center">
                <Form.Item hidden={currentStepIndex !== 1} name="riskTolerance">
                  <Radio.Group size="large">
                    <Flex gap={27} vertical>
                      <Radio value="Preservation">Preservation</Radio>
                      <Radio value="Conservative">Conservative</Radio>
                      <Radio value="Moderate">Moderate</Radio>
                      <Radio value="Growth">Growth</Radio>
                      <Radio value="Aggressive">Aggressive</Radio>
                      <Radio value="unknown">I don't know</Radio>
                    </Flex>
                  </Radio.Group>
                </Form.Item>
              </Flex>

              <Form.Item
                hidden={currentStepIndex !== 2}
                label={token.proposal_copy_investment_amount}
                name="investmentAmount"
              >
                <InputNumber
                  formatter={getCurrencyValue}
                  max={100000000}
                  min={0}
                  onClick={selectValue}
                  parser={cleanCurrencyValue}
                  size="large"
                  step={1000}
                />
              </Form.Item>
              <Form.Item
                hidden={
                  currentStepIndex !== 2 || investmentObjective === "growth"
                }
                label="Year to Start Withdrawals"
                name="yearToStartWithdrawals"
              >
                <InputNumber
                  max={80}
                  min={1}
                  onClick={selectValue}
                  size="large"
                />
              </Form.Item>
              <Form.Item
                hidden={currentStepIndex !== 2}
                label="Length of Investment"
                name="investmentDuration"
              >
                <InputNumber
                  addonAfter={
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Select
                        options={[
                          {
                            label: "Years",
                            value: "years",
                          },
                          {
                            disabled: true,
                            label: "Quarters",
                            value: "quarters",
                          },
                          {
                            disabled: true,
                            label: "Months",
                            value: "months",
                          },
                        ]}
                        style={{ width: 120 }}
                        value="years"
                      />
                    </Form.Item>
                  }
                  max={80}
                  min={10}
                  onClick={selectValue}
                  size="large"
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item hidden name="portfolioRiskTolerance" />
              <Form.Item hidden name="recommendedModelId" />
            </Form>
          </Flex>
        </motion.div>

        <footer>
          <MotionHoverAccent>
            <Button
              disabled={currentStepIndex === 0}
              icon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={onClickBack}
              shape="circle"
              style={{ border: "unset" }}
            />
          </MotionHoverAccent>

          <MotionHoverAccent>
            <Button
              icon={<FontAwesomeIcon icon={faChevronRight} />}
              onClick={onClickForward}
              shape="circle"
              style={{ border: "unset" }}
            />
          </MotionHoverAccent>
        </footer>
      </main>
    </StylesContainer>
  );
};

export default QuestionnaireContentLevel;
